/* useRouteOptimizer — all start-point / travel-mode / optimization state
 * for a route dialog. Shared by Open Houses and Brokerages so the two
 * builders behave identically. Every failure is loud: red banner state,
 * toast, and a `route_optimization` row in Admin → Errors. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useJsApiLoader } from "@react-google-maps/api";
import { reportClientError } from "@/lib/errorReporter";
import {
  optimizeRoute,
  explainFailure,
  googleErrorCode,
  OPTIMIZE_TIMEOUT_MS,
} from "@/lib/routeOptimizer";

const GMAPS_LIBRARIES = ["places"];

export default function useRouteOptimizer({
  open,
  stops,
  optimizationState,
  onApplyOptimized,
  homeAddress,
  homeCoords,
  page,
}) {
  const [startMode, setStartMode] = useState("geo");
  const [customAddress, setCustomAddress] = useState("");
  const [customCommitted, setCustomCommitted] = useState(null); // {address, coords|null}
  const [geoCoords, setGeoCoords] = useState(null);
  const [geoState, setGeoState] = useState("idle"); // idle | requesting | granted | denied | unsupported
  const [geoError, setGeoError] = useState("");
  const [travelMode, setTravelMode] = useState("driving");
  const [optimizing, setOptimizing] = useState(false);
  const [failure, setFailure] = useState(null); // {code, message, explanation}
  const [lastResult, setLastResult] = useState(null); // {totalSeconds, totalMeters, stopCount}
  const runIdRef = useRef(0);
  const lastRunRef = useRef(null); // {inputSig, orderSig}

  const { isLoaded: gmapsLoaded } = useJsApiLoader({
    id: "gmaps-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: GMAPS_LIBRARIES,
  });

  // Ask for GPS once the dialog opens in geo mode.
  useEffect(() => {
    if (!open || startMode !== "geo" || geoState !== "idle") return;
    if (!navigator.geolocation) {
      setGeoState("unsupported");
      setStartMode(homeAddress ? "home" : "custom");
      return;
    }
    setGeoState("requesting");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoState("granted");
      },
      (err) => {
        setGeoError(err.message || "Location permission denied");
        setGeoState("denied");
        setStartMode(homeAddress ? "home" : "custom");
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
    );
  }, [open, startMode, geoState, homeAddress]);

  const commitCustomAddress = useCallback((address, coords = null) => {
    const trimmed = (address || "").trim();
    setCustomAddress(trimmed);
    setCustomCommitted(trimmed ? { address: trimmed, coords } : null);
  }, []);

  const origin = useMemo(() => {
    if (startMode === "geo") return geoCoords;
    if (startMode === "home") return homeAddress ? homeCoords || homeAddress : null;
    if (startMode === "custom") return customCommitted ? customCommitted.coords || customCommitted.address : null;
    return null;
  }, [startMode, geoCoords, homeAddress, homeCoords, customCommitted]);

  const originSig = origin ? (typeof origin === "string" ? origin : `${origin.lat},${origin.lng}`) : "";
  const orderSig = useMemo(() => stops.map((s) => s.id).join(","), [stops]);
  const inputSig = `${originSig}|${travelMode}`;
  const ready = open && gmapsLoaded && !!origin && stops.length >= 2;

  const runOptimization = useCallback(
    async (reason = "auto") => {
      if (!ready) return;
      const runId = ++runIdRef.current;
      setOptimizing(true);
      setFailure(null);
      try {
        const result = await optimizeRoute({ origin, stops, travelMode, timeoutMs: OPTIMIZE_TIMEOUT_MS });
        if (runId !== runIdRef.current) return;
        lastRunRef.current = { inputSig, orderSig: result.orderedIds.join(",") };
        setLastResult({
          totalSeconds: result.totalSeconds,
          totalMeters: result.totalMeters,
          stopCount: stops.length,
        });
        if (onApplyOptimized) onApplyOptimized(result.orderedIds, result.directions);
        if (result.diag && result.diag.chunked) {
          toast.message(
            `Route split into ${result.diag.chunk_count} legs (${stops.length} stops exceeds Google's 25-waypoint limit). Each leg is independently optimized.`,
            { duration: 8000 },
          );
        }
        if (result.diag && result.diag.recovered) {
          // Worked, but only after we re-injected Google's directions module.
          // Log it (warning) so the device with the flaky/blocked load shows
          // up in Admin → Errors with its device_info.
          const modules = (result.diag.reloaded_modules || []).join(", ") || "directions";
          reportClientError(
            "route_optimization",
            `Route optimization recovered on ${page} after reloading Google Maps module(s): ${modules} (${stops.length} stops)`,
            {
              immediate: true,
              fingerprint: `fp_routeopt_${page}_RECOVERED`,
              context: {
                code: "RECOVERED",
                recovered: true,
                page,
                url: typeof window !== "undefined" ? window.location.pathname : "",
                stop_count: stops.length,
                travel_mode: travelMode,
                start_mode: startMode,
                origin: originSig,
                trigger: reason,
                reloaded_modules: result.diag.reloaded_modules || [],
                maps_diag: result.diag,
              },
            },
          );
          toast.message("Google's Directions module had to be reloaded — route optimized on the second try.", {
            duration: 6000,
          });
        }
      } catch (e) {
        if (runId !== runIdRef.current) return;
        const code = googleErrorCode(e);
        const message = String((e && e.message) || e || "");
        const explanation = explainFailure(code);
        setFailure({ code, message, explanation });
        setLastResult(null);
        reportClientError(
          "route_optimization",
          `Route optimization failed (${code}) on ${page} — ${stops.length} stops: ${message}`.slice(0, 500),
          {
            immediate: true,
            stack: (e && e.stack) || "",
            fingerprint: `fp_routeopt_${page}_${code}`,
            context: {
              code,
              page,
              url: typeof window !== "undefined" ? window.location.pathname : "",
              stop_count: stops.length,
              travel_mode: travelMode,
              start_mode: startMode,
              origin: originSig,
              google_message: message,
              trigger: reason,
              timeout_ms: OPTIMIZE_TIMEOUT_MS,
              missing_stop_ids: e && e.missing ? e.missing : undefined,
              maps_diag: (e && e.diag) || undefined,
            },
          },
        );
        toast.error("Route optimization FAILED — stops are NOT optimized", {
          description: explanation,
          duration: 10000,
        });
      } finally {
        if (runId === runIdRef.current) setOptimizing(false);
      }
    },
    [ready, origin, originSig, inputSig, stops, travelMode, startMode, onApplyOptimized, page],
  );

  // Auto-run when the inputs change. A manual re-order is respected until
  // the start point / travel mode / stop set actually changes.
  useEffect(() => {
    if (!ready) return;
    const last = lastRunRef.current;
    if (last && last.inputSig === inputSig && last.orderSig === orderSig) return;
    if (last && last.inputSig === inputSig && optimizationState === "manual") {
      lastRunRef.current = { inputSig, orderSig };
      return;
    }
    lastRunRef.current = { inputSig, orderSig };
    runOptimization("auto");
  }, [ready, inputSig, orderSig, optimizationState, runOptimization]);

  const startReady =
    (startMode === "geo" && geoState === "granted" && !!geoCoords) ||
    (startMode === "home" && !!homeAddress) ||
    (startMode === "custom" && customAddress.trim().length > 0);

  const startAddressForApi = () => {
    if (startMode === "geo" && geoCoords) return `${geoCoords.lat},${geoCoords.lng}`;
    if (startMode === "home" && homeAddress) return homeAddress;
    if (startMode === "custom" && customAddress.trim()) return customAddress.trim();
    return "current_location";
  };

  return {
    gmapsLoaded,
    startMode,
    setStartMode,
    customAddress,
    setCustomAddress,
    commitCustomAddress,
    geoCoords,
    geoState,
    geoError,
    travelMode,
    setTravelMode,
    optimizing,
    failure,
    lastResult,
    startReady,
    startAddressForApi,
    runOptimization,
    canOptimize: gmapsLoaded && stops.length >= 2,
  };
}
